import { PrimitiveTypeIds } from "./PrimitiveTypeIds";
import { serializeUInt8, serializeUInt16, serializeUInt32, serializeInt8, serializeInt16, serializeInt32, serializeFloat32, serializeString, serializeBoolean, deserializeBoolean, deserializeFloat32, deserializeInt16, deserializeInt32, deserializeInt8, deserializeString, deserializeUInt16, deserializeUInt32, deserializeUInt8, } from "./PrimitiveSerializers";
export class PakettoInstance {
    /**
     * @internal
     */
    constructor() {
        this._customSerializers = new Map();
        this._customSerializersByTypeId = new Map();
    }
    addCustomSerializer(type, serialize, deserialize, typeId) {
        if (typeId < 50 || typeId > 255) {
            throw new Error("Custom serializer type identifiers must be between 50 and 255");
        }
        this._internalAddCustomSerializer(type, serialize, deserialize, typeId);
    }
    _internalAddCustomSerializer(type, serialize, deserialize, typeId) {
        const serializer = { serialize, deserialize, typeId };
        this._customSerializers.set(type, serializer);
        this._customSerializersByTypeId.set(typeId, serializer);
    }
    clearCustomSerializers() {
        this._customSerializers.clear();
        this._customSerializersByTypeId.clear();
    }
    serialize(buf, value, offset) {
        const serializer = (value === null || value === void 0 ? void 0 : value.constructor)
            ? this._getCustomSerializerByValue(value)
            : undefined;
        if (serializer) {
            offset = serializeUInt8(buf, serializer.typeId, offset);
            offset = serializer.serialize(buf, value, offset);
        }
        else {
            offset = this._serializePrimitive(buf, value, offset);
        }
        return offset;
    }
    deserialize(buf, offset = 0) {
        const { offset: typeOffset, value: type } = deserializeUInt8(buf, offset);
        const serializer = this._getCustomSerializerByTypeIdentifier(type);
        if (serializer) {
            return serializer.deserialize(buf, typeOffset);
        }
        else {
            return this._deserializePrimitive(buf, typeOffset, type);
        }
    }
    _getCustomSerializerByValue(obj) {
        return this._customSerializers.get(obj.constructor);
    }
    _getCustomSerializerByTypeIdentifier(typeIdentifier) {
        return this._customSerializersByTypeId.get(typeIdentifier);
    }
    _serializePrimitive(buf, value, offset) {
        const type = _getPrimitiveTypeIdentifier(value);
        offset = serializeUInt8(buf, type, offset);
        switch (type) {
            case PrimitiveTypeIds.UInt8:
                offset = serializeUInt8(buf, value, offset);
                break;
            case PrimitiveTypeIds.UInt16:
                offset = serializeUInt16(buf, value, offset);
                break;
            case PrimitiveTypeIds.UInt32:
                offset = serializeUInt32(buf, value, offset);
                break;
            case PrimitiveTypeIds.Int8:
                offset = serializeInt8(buf, value, offset);
                break;
            case PrimitiveTypeIds.Int16:
                offset = serializeInt16(buf, value, offset);
                break;
            case PrimitiveTypeIds.Int32:
                offset = serializeInt32(buf, value, offset);
                break;
            case PrimitiveTypeIds.Float32:
                offset = serializeFloat32(buf, value, offset);
                break;
            case PrimitiveTypeIds.String:
                offset = serializeString(buf, value, offset);
                break;
            case PrimitiveTypeIds.Boolean:
                offset = serializeBoolean(buf, value, offset);
                break;
            case PrimitiveTypeIds.Null:
                break;
            case PrimitiveTypeIds.Undefined:
                break;
            case PrimitiveTypeIds.NaN:
                break;
            case PrimitiveTypeIds.Infinity:
                break;
            case PrimitiveTypeIds.NegativeInfinity:
                break;
            case PrimitiveTypeIds.Object:
                const newVal = value;
                const keys = Object.keys(newVal);
                offset = serializeUInt16(buf, keys.length, offset);
                for (const key of keys) {
                    offset = this.serialize(buf, key, offset);
                    offset = this.serialize(buf, newVal[key], offset);
                }
                break;
            default:
                throw new Error("Unsupported type");
        }
        return offset;
    }
    _deserializePrimitive(buf, offset, type) {
        switch (type) {
            case PrimitiveTypeIds.UInt8:
                return deserializeUInt8(buf, offset);
            case PrimitiveTypeIds.UInt16:
                return deserializeUInt16(buf, offset);
            case PrimitiveTypeIds.UInt32:
                return deserializeUInt32(buf, offset);
            case PrimitiveTypeIds.Int8:
                return deserializeInt8(buf, offset);
            case PrimitiveTypeIds.Int16:
                return deserializeInt16(buf, offset);
            case PrimitiveTypeIds.Int32:
                return deserializeInt32(buf, offset);
            case PrimitiveTypeIds.Float32:
                return deserializeFloat32(buf, offset);
            case PrimitiveTypeIds.String:
                return deserializeString(buf, offset);
            case PrimitiveTypeIds.Boolean:
                return deserializeBoolean(buf, offset);
            case PrimitiveTypeIds.Null:
                return { offset, value: null };
            case PrimitiveTypeIds.Undefined:
                return { offset, value: undefined };
            case PrimitiveTypeIds.NaN:
                return { offset, value: NaN };
            case PrimitiveTypeIds.Infinity:
                return { offset, value: Infinity };
            case PrimitiveTypeIds.NegativeInfinity:
                return { offset, value: -Infinity };
            case PrimitiveTypeIds.Object:
                const { offset: lengthOffset, value: length } = deserializeUInt16(buf, offset);
                offset = lengthOffset;
                const obj = {};
                for (let i = 0; i < length; i++) {
                    const { offset: keyOffset, value: key } = this.deserialize(buf, offset);
                    offset = keyOffset;
                    const { offset: valueOffset, value } = this.deserialize(buf, offset);
                    offset = valueOffset;
                    obj[key] = value;
                }
                return { offset, value: obj };
            default:
                throw new Error("Unsupported type " + type);
        }
    }
    getCustomSerializers() {
        return Array.from(this._customSerializers.values());
    }
}
export function paketto() {
    return new PakettoInstance();
}
function _getPrimitiveTypeIdentifier(value) {
    if (value === null) {
        return PrimitiveTypeIds.Null;
    }
    else if (value === undefined) {
        return PrimitiveTypeIds.Undefined;
    }
    else if (typeof value === "number") {
        if (value % 1 === 0) {
            if (value >= 0) {
                if (value <= 255) {
                    return PrimitiveTypeIds.UInt8;
                }
                else if (value <= 65535) {
                    return PrimitiveTypeIds.UInt16;
                }
                else {
                    return PrimitiveTypeIds.UInt32;
                }
            }
            else {
                if (value >= -128) {
                    return PrimitiveTypeIds.Int8;
                }
                else if (value >= -32768) {
                    return PrimitiveTypeIds.Int16;
                }
                else {
                    return PrimitiveTypeIds.Int32;
                }
            }
        }
        else if (value === Number.POSITIVE_INFINITY) {
            return PrimitiveTypeIds.Infinity;
        }
        else if (value === Number.NEGATIVE_INFINITY) {
            return PrimitiveTypeIds.NegativeInfinity;
        }
        else if (Number.isNaN(value)) {
            return PrimitiveTypeIds.NaN;
        }
        else {
            return PrimitiveTypeIds.Float32;
        }
    }
    else if (typeof value === "string") {
        return PrimitiveTypeIds.String;
    }
    else if (typeof value === "boolean") {
        return PrimitiveTypeIds.Boolean;
    }
    else if (typeof value === "object") {
        return PrimitiveTypeIds.Object;
    }
    throw new Error("Unsupported type");
}
//# sourceMappingURL=Paketto.js.map